import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateTireBatchList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/tire-batch/search');
};

export const useTireBatchList = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue) => {
  const isFetchAll = page === undefined;
  const start = isFetchAll ? 0 : page * rowsPerPage;
  const companies = (selectedCompanies || []).map((c) => c.value).join(',');

  const payload = useMemo(() => {
    if (isFetchAll) return null;
    const fb = new FilterBuilder().setPaging(start, rowsPerPage);

    if (searchKey && searchValue) {
      if (searchKey === 'tire_item_warehouse_name') {
        fb.like('warehouse_name', searchValue, 'AND', 'tire_item');
      } else if (searchKey === 'tire_item_total_price') {
        fb.equal('total_price', Number(searchValue), 'AND', 'tire_item');
      } else if (searchKey === 'tire_item_batch_number') {
        fb.like('batch_number', searchValue, 'AND', 'tire_item');
      } else if (searchKey === 'tire_size_code') {
        fb.like('tire_size_code', searchValue, 'OR', 'tire_item');
        fb.like('tire_size_code_internal', searchValue, 'OR', 'tire_item');
      } else {
        const tireItemFields = ['tire_brand_name', , 'tire_spec_name', 'receive_date', 'supplier'];
        if (tireItemFields.includes(searchKey)) {
          fb.like(searchKey, searchValue, 'AND', 'tire_item');
        } else {
          fb.like(searchKey, searchValue, 'AND');
        }
      }
    }

    fb.addJoin('tire_item', [
      'name',
      'tire_brand_name',
      'tire_size_code',
      'tire_size_code_internal',
      'tire_spec_name',
      'status',
      'total_price',
      'receive_date',
      'supplier',
      'batch_number',
      'warehouse_name'
    ])
      .addJoin('company', ['name'])
      .setOrder(['created_at'], false);
    fb.joins.push({
      name: 'm_tire_item.m_tire_spec',
      column_results: ['pattern', 'original_tread_depth', 'pressure_std'],
      relation: 'tire_spec_id',
      reference: 'id'
    });
    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    }

    return fb.build();
  }, [isFetchAll, start, rowsPerPage, searchKey, searchValue, companies]);

  const {
    data,
    error,
    isLoading,
    mutate: swrMutate
  } = useSWR(payload ? ['/tire-batch/search', payload] : null, fetcher, {
    keepPreviousData: true
  });

  return {
    data: Array.isArray(data?.data) ? data.data : [],
    totalRecord: data?.totalRecord || data?.dataSize || 0,
    isLoading,
    error,
    mutate: swrMutate,
    refetch: swrMutate
  };
};

export const createOrUpdateTireBatch = async (payload) => {
  try {
    const response = await fleetApi.post(`/tire-batch/insert-or-update${getTzParam()}`, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireBatchList();
    return response;
  } catch (error) {
    console.error('Error creating/updating tire batch:', error);
    throw error;
  }
};

export const deleteTireBatch = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/tire-batch/delete/${id}${getTzParam()}`);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireBatchList();
    return response;
  } catch (error) {
    console.error('Error deleting tire batch:', error);
    throw error;
  }
};

const createTireBatchHook =
  (action, defaultExtraQuery = '') =>
  (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue, extraFilters = [], overrideExtraQuery = undefined) => {
    const isFetchAll = page === undefined;
    const start = isFetchAll ? 0 : page * rowsPerPage;
    const companies = (selectedCompanies || []).map((c) => c.value).join(',');

    const payload = useMemo(() => {
      if (isFetchAll) return null;
      const fb = new FilterBuilder().setPaging(start, rowsPerPage);

      if (extraFilters && extraFilters.length > 0) {
        extraFilters.forEach((f) => {
          if (f.operator && typeof fb[f.operator] === 'function') {
            fb[f.operator](f.field, f.value, f.logic || 'AND', f.tableName || null);
          } else {
            fb.equal(f.field, f.value);
          }
        });
      }

      if (searchKey && searchValue) {
        if (searchKey === 'tire_item_warehouse_name') {
          fb.like('warehouse_name', searchValue, 'AND', 'tire_item');
        } else if (searchKey === 'tire_item_total_price') {
          fb.equal('total_price', Number(searchValue), 'AND', 'tire_item');
        } else if (searchKey === 'tire_item_batch_number') {
          fb.like('batch_number', searchValue, 'AND', 'tire_item');
        } else if (searchKey === 'tire_size_code') {
          fb.like('tire_size_code', searchValue, 'OR', 'tire_item');
          fb.like('tire_size_code_internal', searchValue, 'OR', 'tire_item');
        } else {
          const tireItemFields = ['tire_brand_name', 'tire_spec_name', 'receive_date', 'supplier'];
          if (tireItemFields.includes(searchKey)) {
            fb.like(searchKey, searchValue, 'AND', 'tire_item');
          } else {
            fb.like(searchKey, searchValue, 'AND');
          }
        }
      }

      fb.addJoin('tire_item', [
        'name',
        'tire_brand_name',
        'tire_size_code',
        'tire_size_code_internal',
        'tire_spec_name',
        'status',
        'total_price',
        'receive_date',
        'supplier',
        'batch_number',
        'warehouse_id',
        'warehouse_name'
      ]);

      fb.addJoin('company', ['name']);
      fb.joins.push({
        name: 'm_tire_item.m_tire_spec',
        column_results: ['pattern', 'original_tread_depth', 'pressure_std'],
        relation: 'tire_spec_id',
        reference: 'id'
      });
      fb.setOrder(['created_at'], false);

      if (companies) {
        if (companies.includes(',')) {
          fb.in('company_id', companies);
        } else {
          fb.equal('company_id', companies);
        }
      } else {
        fb.filterByCompany();
      }

      return fb.build();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchAll, start, rowsPerPage, searchKey, searchValue, companies, JSON.stringify(extraFilters)]);

    // Build URL: /tire-batch/search?action=xxx&tz=N (single '?' with '&' separator)
    const extraQuery = overrideExtraQuery !== undefined ? overrideExtraQuery : defaultExtraQuery;
    const tz = Math.abs(new Date().getTimezoneOffset() / 60);
    const endpoint = action ? `/tire-batch/search?action=${action}&tz=${tz}${extraQuery}` : `/tire-batch/search?tz=${tz}${extraQuery}`;

    const {
      data,
      error,
      isLoading,
      mutate: swrMutate
    } = useSWR(
      payload ? [endpoint, payload] : null,
      async ([url, body]) => {
        const response = await fleetApi.post(url, body);
        return response;
      },
      {
        keepPreviousData: true
      }
    );

    return {
      data: Array.isArray(data?.data) ? data.data : [],
      totalRecord: data?.totalRecord || data?.dataSize || 0,
      isLoading,
      error,
      mutate: swrMutate,
      refetch: swrMutate
    };
  };
const createTireBatchInventoryHook =
  (action, defaultExtraQuery = '') =>
  (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue, extraFilters = [], overrideExtraQuery = undefined) => {
    const isFetchAll = page === undefined;
    const start = isFetchAll ? 0 : page * rowsPerPage;
    const companies = (selectedCompanies || []).map((c) => c.value).join(',');

    const payload = useMemo(() => {
      if (isFetchAll) return null;
      const fb = new FilterBuilder().setPaging(start, rowsPerPage);

      if (extraFilters && extraFilters.length > 0) {
        extraFilters.forEach((f) => {
          if (f.operator && typeof fb[f.operator] === 'function') {
            fb[f.operator](f.field, f.value, f.logic || 'AND', f.tableName || null);
          } else {
            fb.equal(f.field, f.value);
          }
        });
      }

      if (searchKey && searchValue) {
        if (searchKey === 'tire_item_warehouse_name') {
          fb.like('warehouse_name', searchValue, 'AND', 'tire_item');
        } else if (searchKey === 'tire_item_total_price') {
          fb.equal('total_price', Number(searchValue), 'AND', 'tire_item');
        } else if (searchKey === 'tire_item_batch_number') {
          fb.like('batch_number', searchValue, 'AND', 'tire_item');
        } else if (searchKey === 'tire_size_code') {
          fb.like('tire_size_code', searchValue, 'OR', 'tire_item');
          fb.like('tire_size_code_internal', searchValue, 'OR', 'tire_item');
        } else {
          const tireItemFields = ['tire_brand_name', 'tire_size_code', 'tire_spec_name', 'receive_date', 'supplier'];
          if (tireItemFields.includes(searchKey)) {
            fb.like(searchKey, searchValue, 'AND', 'tire_item');
          } else {
            fb.like(searchKey, searchValue, 'AND');
          }
        }
      }
      fb.addJoin('tire_item', [
        'name',
        'tire_brand_name',
        'tire_size_code',
        'tire_size_code_internal',
        'tire_spec_name',
        'status',
        'total_price',
        'receive_date',
        'supplier',
        'batch_number',
        'warehouse_id',
        'warehouse_name'
      ]);

      fb.addJoin('company', ['name']);
      fb.joins.push({
        name: 'm_tire_item.m_tire_spec',
        column_results: ['pattern', 'original_tread_depth', 'pressure_std'],
        relation: 'tire_spec_id',
        reference: 'id'
      });
      fb.setOrder(['created_at'], false);

      if (companies) {
        if (companies.includes(',')) {
          fb.in('company_id', companies);
        } else {
          fb.equal('company_id', companies);
        }
      } else {
        fb.filterByCompany();
      }

      return fb.build();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchAll, start, rowsPerPage, searchKey, searchValue, companies, JSON.stringify(extraFilters)]);

    // Build URL: /tire-batch/search?action=xxx&tz=N (single '?' with '&' separator)
    const extraQuery = overrideExtraQuery !== undefined ? overrideExtraQuery : defaultExtraQuery;
    const tz = Math.abs(new Date().getTimezoneOffset() / 60);
    const endpoint = action ? `/tire-batch/search?action=${action}&tz=${tz}${extraQuery}` : `/tire-batch/search?tz=${tz}${extraQuery}`;

    const {
      data,
      error,
      isLoading,
      mutate: swrMutate
    } = useSWR(
      payload ? [endpoint, payload] : null,
      async ([url, body]) => {
        const response = await fleetApi.post(url, body);
        return response;
      },
      {
        keepPreviousData: true
      }
    );

    return {
      data: Array.isArray(data?.data) ? data.data : [],
      totalRecord: data?.totalRecord || data?.dataSize || 0,
      isLoading,
      error,
      mutate: swrMutate,
      refetch: swrMutate
    };
  };

export const useTireBatchListAll = createTireBatchHook('all');
export const useTireBatchListInventory = createTireBatchInventoryHook('remove', '&is_need_action=false');
export const useTireBatchListRepairScrap = createTireBatchHook('remove', '&is_need_action=true');

export const getTireBatchList = async (selectedCompanies = [], searchKey, searchValue) => {
  try {
    const companies = (selectedCompanies || []).map((c) => c.value).join(',');
    const fb = new FilterBuilder().setPaging(0, 9999);

    if (searchKey && searchValue) {
      if (searchKey === 'tire_item_warehouse_name') {
        fb.like('warehouse_name', searchValue, 'AND', 'tire_item');
      } else if (searchKey === 'tire_item_total_price') {
        fb.equal('total_price', Number(searchValue), 'AND', 'tire_item');
      } else if (searchKey === 'tire_item_batch_number') {
        fb.like('batch_number', searchValue, 'AND', 'tire_item');
      } else if (searchKey === 'tire_size_code') {
        fb.like('tire_size_code', searchValue, 'OR', 'tire_item');
        fb.like('tire_size_code_internal', searchValue, 'OR', 'tire_item');
      } else {
        const tireItemFields = ['tire_brand_name', 'tire_size_code', 'tire_spec_name', 'receive_date', 'supplier'];
        if (tireItemFields.includes(searchKey)) {
          fb.like(searchKey, searchValue, 'AND', 'tire_item');
        } else {
          fb.like(searchKey, searchValue, 'AND');
        }
      }
    }

    fb.addJoin('tire_item', [
      'name',
      'tire_brand_name',
      'tire_size_code',
      'tire_size_code_internal',
      'tire_spec_name',
      'status',
      'total_price',
      'receive_date',
      'supplier',
      'warehouse_id',
      'warehouse_name',
      'batch_number'
    ]);
    fb.joins.push({
      name: 'm_tire_item.m_tire_spec',
      column_results: ['pattern', 'original_tread_depth', 'pressure_std'],
      relation: 'tire_spec_id',
      reference: 'id'
    });
    fb.addJoin('company', ['name']);
    fb.addJoin('warehouse', ['name']);
    fb.setOrder(['created_at'], false);

    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    }

    const payload = fb.build();
    const tz = Math.abs(new Date().getTimezoneOffset() / 60);
    const response = await fleetApi.post(`/tire-batch/search?action=all&tz=${tz}`, payload);
    const fetchedData = response?.data?.data || response?.data;
    return Array.isArray(fetchedData) ? fetchedData : [];
  } catch (error) {
    console.error('Error fetching tire batch list:', error);
    return [];
  }
};

export const uploadTireScrapImage = async (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fleetApi.post(`/tire-batch/uploadfile/${id}?status=scrap`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response;
};
